import React, { useEffect, useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Popover,
  Badge,
  Dialog,
  DialogContent,
  Button as MuiButton,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { BellOutlined } from "@ant-design/icons";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { List, Typography as AntTypography } from "antd";
import Fade from "@mui/material/Fade";
import BaseService from "../../service/base.service";
import Notifications from "../../models/notification";
import { useNavigate } from 'react-router-dom'; // for React Router v6
import { useAppDispatch } from "../../hooks/redux-hooks";
import { logout } from "../../slices/authSlice";

interface NotificationBellProps {
  initialNotifications?: Notifications[];
}

const SlideRightTransition = React.forwardRef(function SlideRightTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

// ✅ Only HIGH importance color
const importanceColors: Record<string, string> = {
  HIGH: "red",
};

const NotificationBell: React.FC<NotificationBellProps> = ({
  initialNotifications = [],
}) => {
  const [allNotifications, setAllNotifications] = useState<Notifications[]>(initialNotifications);
  const [readNotifications, setReadNotifications] = useState<Notifications[]>(initialNotifications);
  const [unreadNotifications, setUnreadNotifications] = useState<Notifications[]>(initialNotifications);
  const [selectedNotif, setSelectedNotif] = useState<Notifications | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0); // 0=All,1=Read,2=Unread
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Popover state
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchorEl);

  useEffect(() => {
    fetchAllNotifications();
    fetchReadNotifications();
    fetchUnreadNotifications();
  }, []);

  const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setSelectedTab(newValue);

  const fetchAllNotifications = async () => {
    try {
      const rp = await BaseService.getAll<Notifications>("/notifications/all");
      if (rp.Status) setAllNotifications(rp.Data || []);
    } catch (err) { console.error(err); }
  };
  const fetchReadNotifications = async () => {
    try {
      const rp = await BaseService.getAll<Notifications>("/notifications/read");
      if (rp.Status) setReadNotifications(rp.Data || []);
    } catch (err) { console.error(err); }
  };
  const fetchUnreadNotifications = async () => {
    try {
      const rp = await BaseService.getAll<Notifications>("/notifications/unread");
      if (rp.Status) setUnreadNotifications(rp.Data || []);
    } catch (err) { console.error(err); }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate("/login");
    } catch (e) {
      console.error(e);
    }
  };

  const handleShowAndMarkAsRead = async (item: Notifications) => {
    await BaseService.update<void>("/notifications/read/", item.id, null);
    setSelectedNotif(item);
    fetchAllNotifications();
    fetchReadNotifications();
    fetchUnreadNotifications();
  };

  const handleDelete = async (id: number) => {
    setSelectedNotif(null);
    await BaseService.delete("/notifications/", id);
    fetchAllNotifications();
    fetchReadNotifications();
    fetchUnreadNotifications();
  };

  const filteredNotifications = (() => {
    if (selectedTab === 1) return readNotifications;
    if (selectedTab === 2) return unreadNotifications;
    return allNotifications;
  })();

  return (
    <>
      <Badge badgeContent={unreadNotifications.length} color="error">
        <BellOutlined
          style={{ fontSize: 20, color: "#555", cursor: "pointer" }}
          onClick={handleNotifClick}
        />
      </Badge>

      <Popover
        open={notifOpen}
        onClose={handleNotifClose}
        anchorReference="none"
        PaperProps={{
          sx: {
            position: "fixed",
            top: 96,
            right: 16,
            width: 380,
            height: "calc(100vh - 176px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
            borderRadius: 3,
            boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
            overflow: "hidden",
          },
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            background: "linear-gradient(90deg, #1677fa 0%, #52c41a 100%)",
            color: "#fff",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>

          <IconButton
            onClick={handleNotifClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "#fafafa",
            minHeight: 38,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              minHeight: 38,
            },
          }}
        >
          {["All", "Read", "Unread"].map((label, idx) => {
            const count =
              idx === 0
                ? allNotifications.length
                : idx === 1
                  ? readNotifications.length
                  : unreadNotifications.length;

            return (
              <Tab
                key={label}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {label}
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        backgroundColor: "#E8F0FF",
                        color: "#3B6FFF",
                        fontSize: 11,
                        height: 18,
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        {/* Notification List */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2, backgroundColor: "#fff" }}>
          {filteredNotifications.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: "#999",
              }}
            >
              <BellOutlined style={{ fontSize: 36, marginBottom: 8, opacity: 0.6 }} />
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={filteredNotifications}
              renderItem={(item) => {
                const isUnread = !item.read;

                return (
                  <List.Item
                    style={{
                      cursor: "pointer",
                      background: isUnread ? "#f0f7ff" : "transparent",
                      borderLeft: isUnread
                        ? "4px solid #1677ff"
                        : "4px solid transparent",
                      padding: "8px 12px",
                      marginBottom: 6,
                      borderRadius: 6,
                      transition: "background 0.2s ease",
                    }}
                    onClick={() => handleShowAndMarkAsRead(item)}
                  >
                    <List.Item.Meta
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isUnread ? 600 : 400,
                              fontSize: 13,
                            }}
                          >
                            {item.title}
                          </Typography>

                          {item.importance === "HIGH" && (
                            <Chip
                              label="High"
                              size="small"
                              sx={{
                                backgroundColor: "#ff4d4f",
                                color: "#fff",
                                fontWeight: 500,
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      }
                      description={
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          {item.time}
                        </Typography>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Box>
      </Popover>

      {/* Dialog */}
      <Dialog
        open={Boolean(selectedNotif)}
        onClose={() => setSelectedNotif(null)}
        maxWidth="xs"
        fullWidth
        slots={{ transition: SlideRightTransition }}
        slotProps={{ transition: { unmountOnExit: true } }}
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: 12 } }}
      >
        <Box sx={{ backgroundColor: "#E8F0FF", p: 2, textAlign: "center", position: "relative" }}>
          <Typography variant="subtitle1" sx={{ color: "#2B6CB0", fontWeight: 600 }}>
            {selectedNotif?.title}
          </Typography>
          <IconButton onClick={() => setSelectedNotif(null)} sx={{ color: "#2B6CB0", position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>

          {/* ✅ Dialog Chip only for HIGH */}
          {selectedNotif?.importance === "HIGH" && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
              <Chip
                label="High"
                sx={{
                  backgroundColor: importanceColors.HIGH,
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </Box>

        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", minHeight: 140 }}>
          {selectedNotif?.time && (
            <Box sx={{ alignSelf: "flex-end", display: "flex", alignItems: "center", color: "text.secondary", mb: 2 }}>
              <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
              <Typography variant="caption">{selectedNotif.time}</Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: "center", mb: 4, whiteSpace: "pre-line" }}>
            {selectedNotif?.description || "No additional details available."}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MuiButton
              variant="contained"
              color="error"
              size="medium"
              onClick={() => handleDelete(selectedNotif!.id)}
              sx={{ minWidth: 120 }}
            >
              Delete
            </MuiButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
